import {
  ChartData,
  ReportContext,
  donutColors,
  getTremorOptions,
} from '@bm-js/atlas-analytics'
import { Card, CardBody, CardHeader } from '@heroui/card'
import { Spinner } from '@heroui/spinner'
import { CText } from '@bm-js/atlascloud-ui'
import { Legend } from '@tremor/react'
import { useMemo } from 'react'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

type Props = {
  context: ReportContext
  data: ChartData | null
  loading: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ChartComponent: any
}

// prettier-ignore
const reportMap: Record<ReportContext, TrKey> = {
  [ReportContext.uniqueUsersOverTime]: TrKey.analyticsUniqueUsersOverTime,
  [ReportContext.usersPerRoute]: TrKey.analyticsUsersPerRoute,
  [ReportContext.usersPerReferer]: TrKey.analyticsUsersPerReferer,
  [ReportContext.usersPerDevice]: TrKey.analyticsUsersPerDevice,
  [ReportContext.usersPerPlatform]: TrKey.analyticsUsersPerPlatform,
  [ReportContext.usersPerOS]: TrKey.analyticsUsersPerOS,
  [ReportContext.usersPerVendor]: TrKey.analyticsUsersPerVendor,
  [ReportContext.usersPerCountry]: TrKey.analyticsUsersPerCountry,
  [ReportContext.avgEngagementTimeOverTime]: TrKey.analyticsAvgEngagementTimeOverTime,
  [ReportContext.avgEngagementTimePerRoute]: TrKey.analyticsAvgEngagementTimePerRoute,
}

const AnalyticsGraph = ({ context, data, loading, ChartComponent }: Props) => {
  const { tr } = useTr()
  const tremorOptions = useMemo(() => {
    if (!data) return {}
    return getTremorOptions(data, data.type === 'Doughnut' ? 'h-80' : 'h-96')
  }, [data])

  return (
    <Card>
      <CardHeader>
        <CText tag='h2' className='text-xl'>
          {tr(reportMap[context])}
        </CText>
      </CardHeader>
      <CardBody>
        <div className='relative'>
          {loading && (
            <div className='absolute inset-0 h-80 w-full flex items-center justify-center text-primary-400'>
              <Spinner color='current' />
            </div>
          )}

          {data?.type === 'Doughnut' && (
            <Legend
              categories={data.data.labels}
              colors={donutColors}
              className='mb-10'
            />
          )}
          <div
            className={`mb-4 transition-opacity ${
              loading ? 'opacity-0' : 'opacity-100'
            }`}
          >
            <ChartComponent {...tremorOptions} />
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default AnalyticsGraph
